import * as Yup from 'yup'

import { CENTIMETERS_PER_INCH } from '../../../../shared/dictionaries/unitConversion.dictionary'
import { OverallOrderDetailsFormValues } from './OverallOrderDetails.types'

export const USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_INCHES = 108
export const USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM = USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_INCHES * CENTIMETERS_PER_INCH

export const getCombinedLengthPlusGirthCm = (lengthCm: number, widthCm: number, heightCm: number): number =>
  lengthCm + 2 * widthCm + 2 * heightCm

export const INITIAL_OVERALL_ORDER_DETAILS_VALUES: OverallOrderDetailsFormValues = {
  lengthCm: '',
  widthCm: '',
  heightCm: '',
  packageWeightValue: '',
  weightUnit: 'lbs',
  packageType: 'box',
  fragileHandling: false,
  repackagingAllowed: false,
  specialHandlingNotes: ''
}

export const PACKAGE_TYPE_OPTIONS = [
  { value: 'box', label: 'Box' },
  { value: 'padded_envelope', label: 'Padded Envelope' },
  { value: 'tube', label: 'Tube' },
  { value: 'flat_rate_box', label: 'Flat Rate Box' }
]

export const WEIGHT_UNIT_OPTIONS = [
  { value: 'kg', label: 'kg' },
  { value: 'lbs', label: 'lbs' }
]

const dimensionFields = {
  lengthCm: Yup.number()
    .typeError('Must be a valid number')
    .positive('Must be greater than 0')
    .max(
      USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM,
      `Must be ${Math.round(USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM)} cm or less`
    )
    .required('Length is required')
    .test(
      'usps-combined-length-girth',
      `Package exceeds USPS size limit. Length + girth must be ${USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_INCHES} inches (${Math.round(USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM)} cm) or less.`,
      function (_value) {
        const parent = this.parent as OverallOrderDetailsFormValues

        const lengthCm = Number(parent.lengthCm)
        const widthCm = Number(parent.widthCm)
        const heightCm = Number(parent.heightCm)

        if (!lengthCm || !widthCm || !heightCm) return true

        const combined = getCombinedLengthPlusGirthCm(lengthCm, widthCm, heightCm)

        return combined <= USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM
      }
    ),
  widthCm: Yup.number()
    .typeError('Must be a valid number')
    .positive('Must be greater than 0')
    .max(
      USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM,
      `Must be ${Math.round(USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM)} cm or less`
    )
    .required('Width is required'),
  heightCm: Yup.number()
    .typeError('Must be a valid number')
    .positive('Must be greater than 0')
    .max(
      USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM,
      `Must be ${Math.round(USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM)} cm or less`
    )
    .required('Height is required'),
  packageWeightValue: Yup.number()
    .typeError('Must be a valid number')
    .positive('Must be greater than 0')
    .required('Package weight is required')
}

const optionalDimensionFields = {
  lengthCm: Yup.number().optional(),
  widthCm: Yup.number().optional(),
  heightCm: Yup.number().optional(),
  packageWeightValue: Yup.number()
    .typeError('Must be a valid number')
    .positive('Must be greater than 0')
    .required('Package weight is required')
}

export const getOverallOrderDetailsValidationSchema = (dimensionsEnabled: boolean) =>
  Yup.object({
    ...(dimensionsEnabled ? dimensionFields : optionalDimensionFields),
    weightUnit: Yup.string().oneOf(['kg', 'lbs']).required(),
    packageType: Yup.string().required('Package type is required'),
    fragileHandling: Yup.boolean(),
    repackagingAllowed: Yup.boolean(),
    specialHandlingNotes: Yup.string()
  })

export const overallOrderDetailsValidationSchema = getOverallOrderDetailsValidationSchema(true)
